import { useTenantConfig } from 'contexts';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import React, { useEffect } from 'react';
import { useComboPopupStore } from 'stores/comboPopupStore';

// Lazy-loaded so the popup (and combo selection deps) stay off the critical
// _app bundle. Both stay off the bundle until a product is selected; we pick the
// platform variant at render time so mobile gets the native full-screen UI
// instead of the desktop modal.
const AddToCartComboPopupDesktop = dynamic(() =>
  import('components/desktop/organisms/Combo/AddToCartComboPopup').then(
    (mod) => mod.AddToCartComboPopup,
  ),
);

const AddToCartComboPopupMobile = dynamic(() =>
  import('components/mobile/organisms/Combo/AddToCartComboPopup').then(
    (mod) => mod.AddToCartComboPopup,
  ),
);

/**
 * Single global host that renders the add-to-cart combo popup. Mounted once in
 * _app. The popup state lives in the zustand `useComboPopupStore`; this
 * component only bridges the React-only bits (feature flag + route changes)
 * into the store and renders the popup. The pure helper
 * createHandleEventAndCtaButton opens it via useComboPopupStore.getState().open()
 * from any product-card surface.
 */
export const ComboPopupRenderer: React.FC = () => {
  const { enableComboPopupOnATC, isMobile } = useTenantConfig();
  const setEnabled = useComboPopupStore((s) => s.setEnabled);
  const selectedProduct = useComboPopupStore((s) => s.selectedProduct);
  const close = useComboPopupStore((s) => s.close);
  const router = useRouter();

  const enabled = !!enableComboPopupOnATC;

  useEffect(() => {
    setEnabled(enabled);
  }, [enabled, setEnabled]);

  useEffect(() => {
    // Close popup when navigating to another page.
    close();
  }, [router.asPath, close]);

  if (!enabled || !selectedProduct) return null;

  const AddToCartComboPopup = isMobile
    ? AddToCartComboPopupMobile
    : AddToCartComboPopupDesktop;

  return (
    <AddToCartComboPopup
      key={selectedProduct.sku}
      product={selectedProduct}
      onClose={close}
    />
  );
};
